import { type ReactNode, useState } from 'react';
import { IconChevronDown, IconChevronUp } from '@optiaxiom/icons';
import { Badge, Box, Flex, Text, theme } from '@optiaxiom/react';

import {
  effectiveSectionId,
  type StickyCard as StickyCardModel,
} from '@frontend/types/stickyboard';

import { useStickyboardDnd } from '../hooks/useStickyboardDnd';

import { StickyCard } from './StickyCard';

type Props = {
  stickies: StickyCardModel[];
  label: string;
  stackId: string;
};

// A small palette so every stack gets a stable, distinct boundary colour.
const STACK_PALETTE = [
  '#6366f1', // indigo
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#8b5cf6', // violet
  '#ef4444', // red
  '#14b8a6', // teal
] as const;

// How many card titles to list in the collapsed pile before "+N more".
const PREVIEW_LIMIT = 8;

/** Stable accent colour for a stack, derived from its id (same id → same hue). */
export function stackAccent(stackId: string): string {
  let h = 0;
  for (let i = 0; i < stackId.length; i++) h = (h * 31 + stackId.charCodeAt(i)) >>> 0;
  return STACK_PALETTE[h % STACK_PALETTE.length];
}

/**
 * Smoothly reveals/hides its content by animating the grid row 0fr↔1fr (no
 * height measuring, no library). Content stays mounted so BOTH opening and
 * closing flow — there's no abrupt drop. When closed the inner is clipped and
 * made non-interactive so the hidden cards can't steal clicks or focus.
 */
function Reveal({
  open,
  onClick,
  testId,
  children,
}: {
  open: boolean;
  onClick?: () => void;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows 240ms ease',
      }}
    >
      <div
        data-testid={testId}
        data-open={open}
        aria-hidden={!open}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick && open ? 0 : undefined}
        onClick={open ? onClick : undefined}
        onKeyDown={
          open && onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        style={{
          overflow: 'hidden',
          minHeight: 0,
          pointerEvents: open ? undefined : 'none',
          cursor: open && onClick ? 'pointer' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A pile of related stickies (same stack). Collapsed it reads as a stack of
 * cards — a coloured top accent + offset shadows peeking behind — and lists the
 * member titles (most-recently-arrived first) so the user can scan the cluster
 * without opening it. Click to fan it open; click the header to collapse. The
 * accent colour is stable per stack id so a cluster is recognisable at a glance.
 */
export function StickyStack({ stickies, label, stackId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const accent = stackAccent(stackId);
  const dnd = useStickyboardDnd();
  // Members share a section; use it so a card dropped here joins the pile in the
  // right lane, and a whole-pile drag carries its current lane.
  const section = stickies[0] ? (effectiveSectionId(stickies[0]) ?? '') : '';

  // Newest arrival first (created_at = when the card landed on the board).
  const sorted = [...stickies].sort(
    (a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0)
  );
  const preview = sorted.slice(0, PREVIEW_LIMIT);
  const overflow = sorted.length - preview.length;

  const header = (
    <Flex
      flexDirection="row"
      gap="8"
      alignItems="center"
      justifyContent="space-between"
      style={{ cursor: 'pointer', padding: '10px 12px' }}
      onClick={() => setExpanded(e => !e)}
    >
      <Flex flexDirection="row" gap="8" alignItems="center" style={{ minWidth: 0 }}>
        <Box
          style={{ width: 8, height: 8, borderRadius: 9999, background: accent, flex: 'none' }}
        />
        <Text
          fontWeight="600"
          fontSize="sm"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </Text>
        <Badge>{stickies.length}</Badge>
      </Flex>
      {expanded ? <IconChevronUp /> : <IconChevronDown />}
    </Flex>
  );

  return (
    <Box
      data-testid="sticky-stack"
      data-expanded={expanded}
      // Collapsed, the whole pile is draggable (move the cluster). Expanded, the
      // pile is not draggable so its inner cards drag individually. Always a drop
      // target — drop a card on it to join the stack.
      draggable={!expanded}
      onDragStart={dnd.startStackDrag(stackId, section, stickies)}
      onDragEnd={dnd.endDrag}
      onDragOver={dnd.allowDrop}
      onDragLeave={dnd.leaveDrop}
      onDrop={dnd.dropOnStack(stackId, section)}
      style={{
        position: 'relative',
        // Zero outer padding so the cards inside sit near full cell width (not
        // squeezed vs standalone cards); the boundary hugs them. overflow:hidden
        // keeps content inside the rounded corners.
        overflow: 'hidden',
        borderTop: `3px solid ${accent}`,
        border: `1px solid ${expanded ? accent : theme.colors['border.tertiary']}`,
        borderRadius: 10,
        background: theme.colors['bg.default'],
        padding: 0,
        // Collapsed: offset colour-tinted shadows read as a couple of cards
        // behind the top one. Expanded: a plain lift. Transitioned so toggling
        // settles instead of snapping.
        boxShadow: expanded
          ? '0 1px 4px rgba(0,0,0,0.10)'
          : `2px 2px 0 0 ${accent}40, 4px 4px 0 0 ${accent}22, 0 1px 4px rgba(0,0,0,0.10)`,
        transition: 'box-shadow 240ms ease, border-color 240ms ease',
      }}
    >
      {header}

      {/* Collapsed: each member as a peeking card layer — a little hand of
          playing cards. Newest sits on top (full), the rest fan out behind it
          with their title (the "rank") peeking. */}
      <Reveal open={!expanded} onClick={() => setExpanded(true)} testId="sticky-stack-preview">
        <Box style={{ padding: '0 10px 10px' }}>
          {preview.map((s, i) => (
            <Box
              key={s.sticky_id}
              style={{
                position: 'relative',
                // Newest on top; each card behind peeks a strip below it, so the
                // pile reads as a hand of cards with every title (the "rank")
                // showing. No horizontal drift — right edges stay aligned so
                // nothing is clipped by the container.
                zIndex: preview.length - i,
                marginTop: i === 0 ? 0 : -10,
                background: theme.colors['bg.default'],
                border: `1px solid ${theme.colors['border.tertiary']}`,
                borderLeft: `3px solid ${accent}`,
                borderRadius: 7,
                boxShadow: '0 2px 4px rgba(0,0,0,0.16)',
                padding: '7px 10px',
              }}
            >
              <Text
                fontSize="xs"
                color="fg.secondary"
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.title}
              </Text>
            </Box>
          ))}
          {overflow > 0 && (
            <Text
              fontSize="xs"
              color="fg.tertiary"
              style={{ display: 'block', paddingTop: 8, paddingLeft: 4 }}
            >
              +{overflow} more
            </Text>
          )}
        </Box>
      </Reveal>

      {/* Expanded: the real cards, fully inside the boundary. The small
          horizontal+bottom padding keeps each card (and its shadow) clear of
          the container's clip edge so they sit nestled within, not flush. */}
      <Reveal open={expanded} testId="sticky-stack-cards">
        <Flex flexDirection="column" gap="8" style={{ padding: '0 3px 8px' }}>
          {sorted.map(s => (
            <StickyCard key={s.sticky_id} sticky={s} />
          ))}
        </Flex>
      </Reveal>
    </Box>
  );
}
